import { type ReactNode, createContext, useContext, useState } from 'react';
import type { Screen } from '../common/screens';
import { useMapElement } from './MapElementProvider';

interface Props {
    defaultScreen: Screen;
    children: ReactNode;
}

interface Context {
    approvalToEdit: ApprovalToEdit;
    onCreateApproval: () => void;
}

interface Approval {
    id: string;
    title: string;
    description: string;
    disabled: boolean;
    order: number;
    minimumApprovalsRequired: number;
    minimumDenialsRequired: number;
    approvedOutcomeId: string | null;
    deniedOutcomeId: string | null;
}

interface ApprovalToEdit {
    approval: Approval;
    index: number;
    isEditing: boolean;
}

const Context = createContext<Context | undefined>(undefined);

const ApprovalProvider = ({ children }: Props) => {
    const { onSwitchScreen } = useMapElement();

    const initialApproval: Approval = {
        id: '',
        title: '',
        description: '',
        disabled: false,
        order: 0,
        minimumApprovalsRequired: 1,
        minimumDenialsRequired: 1,
        approvedOutcomeId: null,
        deniedOutcomeId: null,
    };

    const [approvalToEdit, setApprovalToEdit] = useState<ApprovalToEdit>({
        approval: initialApproval,
        index: 0,
        isEditing: false,
    });

    const onCreateApproval = () => {
        setApprovalToEdit({
            approval: { ...initialApproval, order: 0 },
            index: 0,
            isEditing: false,
        });

        onSwitchScreen('approvalDetails');
    };

    const contextValue = {
        approvalToEdit,
        onCreateApproval,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const useApproval = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('useApproval must be used within an ApprovalProvider');
    }
    return context;
};

export { ApprovalProvider, useApproval };
